import React from 'react';
import { Link } from 'react-router-dom';
import StaticPageLayout from '../components/StaticPageLayout';
import { CheckCircle, FileText, AlertCircle } from 'lucide-react';

const AuthorGuidelinesPage = () => (
  <StaticPageLayout
    overline="Submission Policies"
    title="Author Guidelines"
    description="Essential guidance for researchers preparing to submit manuscripts to MAH Quantum Research Institute journals. Read this page carefully before beginning your submission."
    breadcrumbs={[
      { label: 'Submission Policies', path: '/submission-process' },
      { label: 'Author Guidelines', path: '/author-guidelines' },
    ]}
  >
    <div className="space-y-12">

      {/* Who Can Submit */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Who Can Submit</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          MAH Quantum Research Institute accepts submissions from a broad range of research contributors, including:
        </p>
        <ul className="space-y-2">
          {[
            'Academic researchers affiliated with universities and research institutions',
            'PhD scholars and postdoctoral researchers',
            'Professors and faculty members at recognised academic institutions',
            'Independent researchers with demonstrable expertise in the relevant field',
            'Professional researchers working in industry, government, or policy research settings',
          ].map(item => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-[#4B5563]">
              <CheckCircle size={14} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      {/* General Requirements */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">General Requirements</h2>
        <div className="space-y-3">
          {[
            {
              title: 'Originality',
              desc: 'Submitted manuscripts must be original, unpublished work not currently under review at any other journal or conference proceeding. Simultaneous submission to multiple journals is strictly prohibited.'
            },
            {
              title: 'Language',
              desc: 'Manuscripts must be written in clear, grammatically correct English. Authors for whom English is not a first language are encouraged to seek professional language editing support prior to submission.'
            },
            {
              title: 'Ethical Compliance',
              desc: 'All research involving human subjects, patient data, or animal experimentation must comply with applicable ethical standards. Authors must provide evidence of ethical approval (e.g., IRB or equivalent) where required.'
            },
            {
              title: 'Authorship',
              desc: 'All listed authors must have made a substantive intellectual contribution to the research, including conception, design, data collection, analysis, or manuscript drafting. Gift authorship, ghost authorship, and guest authorship are not permitted.'
            },
            {
              title: 'Data Integrity',
              desc: 'Reported data must be accurate, verifiable, and free from fabrication or falsification. Where datasets are used, authors are encouraged to provide data availability statements and links to open repositories.'
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-4 bg-white">
              <h4 className="font-semibold text-[#1F1F1F] text-sm mb-1.5">{item.title}</h4>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Manuscript Structure */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Manuscript Structure</h2>
        <p className="text-[#4B5563] text-sm leading-relaxed mb-4">
          A complete manuscript submission should include the following components in the order listed:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-sm text-[#4B5563] pl-2">
          {[
            'Title Page — title, full author names, affiliations, ORCID IDs (if available), and corresponding author contact details',
            'Abstract — structured abstract of 150–250 words covering background, objective, methods, results, and conclusion',
            'Keywords — 4 to 8 terms relevant to the subject matter and aligned with standard indexing vocabularies',
            'Introduction — background, research problem, objectives, and brief overview of the study',
            'Methodology — detailed description of research design, data collection, and analysis methods',
            'Results — clear presentation of findings, with tables and figures numbered sequentially',
            'Discussion — interpretation of results in the context of existing literature',
            'Conclusion — summary of contributions, limitations, and future research directions',
            'Declarations — ethical approval, conflict of interest, funding acknowledgements',
            'References — complete, consistently formatted reference list (APA, Vancouver, or IEEE style)',
            'Figures, Tables, and Appendices — if applicable, supplied at sufficient resolution',
          ].map(item => (
            <li key={item} className="leading-relaxed">{item}</li>
          ))}
        </ol>
      </section>

      {/* Formatting Requirements */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Formatting Requirements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'File Format', value: 'PDF or DOCX (PDF preferred for initial submission)' },
            { label: 'Font & Size', value: 'Times New Roman or Arial, 12pt body text' },
            { label: 'Line Spacing', value: 'Double-spaced throughout (including references)' },
            { label: 'Margins', value: '2.5 cm (1 inch) on all sides' },
            { label: 'Word Count', value: 'Research articles: 4,000–10,000 words (excluding references); Review articles: up to 15,000 words' },
            { label: 'Page Numbering', value: 'Continuous, starting from the title page' },
            { label: 'Figure Resolution', value: 'Minimum 300 DPI for all images and diagrams' },
            { label: 'Tables', value: 'Editable format in the document; not embedded as images' },
          ].map(item => (
            <div key={item.label} className="border border-[#E5E7EB] px-4 py-3 bg-white flex gap-3">
              <FileText size={13} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#374151] mb-0.5">{item.label}</p>
                <p className="text-xs text-[#6B7280] leading-relaxed">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reference & Citation Style */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Reference &amp; Citation Style</h2>
        <p className="text-[#4B5563] text-sm leading-relaxed mb-4">
          Authors may use any of the following citation styles, provided the chosen style is applied consistently throughout the manuscript. The preferred style for each journal is indicated in that journal's individual author guidelines:
        </p>
        <div className="space-y-3">
          {[
            { style: 'APA (7th Edition)', suitable: 'Social sciences, behavioural sciences, education' },
            { style: 'Vancouver Style', suitable: 'Biomedical sciences, clinical research, healthcare' },
            { style: 'IEEE Style', suitable: 'Engineering, computer science, technology' },
            { style: 'ACS Style', suitable: 'Chemistry, materials science, chemical engineering' },
          ].map(item => (
            <div key={item.style} className="flex items-start gap-3 border border-[#E5E7EB] px-4 py-3 bg-white">
              <CheckCircle size={13} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-semibold text-[#1F1F1F]">{item.style}</span>
                <span className="text-sm text-[#6B7280] ml-2">— Recommended for: {item.suitable}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Important Notices */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Important Notices</h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 px-4 py-4">
            <AlertCircle size={14} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#174EA6] leading-relaxed">
              Manuscripts that do not conform to the formatting and structural requirements outlined above may be returned to authors prior to editorial review. Authors are advised to review the checklist carefully before submission.
            </p>
          </div>
          <div className="flex items-start gap-3 bg-[#F5F7FA] border border-[#E5E7EB] px-4 py-4">
            <AlertCircle size={14} className="text-[#6B7280] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#6B7280] leading-relaxed">
              Submission of a manuscript implies that the work has not been published previously in any form, is not under consideration elsewhere, and that all co-authors have approved the submission.
            </p>
          </div>
        </div>
      </section>

      {/* Before You Submit */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Before You Submit</h2>
        <p className="text-[#4B5563] text-sm leading-relaxed">
          Authors are strongly encouraged to review the{' '}
          <Link to="/review-criteria" className="text-[#1A73E8] hover:underline">Review Criteria</Link>,{' '}
          <Link to="/manuscript-preparation" className="text-[#1A73E8] hover:underline">Manuscript Preparation Guide</Link>, and{' '}
          <Link to="/publication-ethics" className="text-[#1A73E8] hover:underline">Publication Ethics Policy</Link>{' '}
          before preparing their submission. This ensures full alignment with the standards applied during editorial screening and peer review.
        </p>
      </section>

    </div>
  </StaticPageLayout>
);

export default AuthorGuidelinesPage;
